import React, { useState, useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import UserSidebar from "@/components/UserSidebar";
import {
  Coins,
  Wallet,
  History,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Clock
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/components/AuthContext";
import { fetchAccountTransactions, AccountTransaction } from "@/services/customerService";

// operation_type の日本語ラベル
const operationTypeLabels: Record<string, string> = {
  'PointConversion': 'PT交換',
  'lottery_draw': 'くじ使用',
  'oripa_draw': 'オリパ抽選',
  'kuji_checkout': 'くじ購入',
  'kuji_ec_purchase': 'くじ購入',
  'point_charge': 'ポイント購入',
  'refund': '返金',
  'shipping_fee': '送料',
  'TenantCompensation': '運営補填',
  'Referral': '友だち紹介報酬',
  'CouponRedemption': 'クーポン利用',
  'bonus': 'ボーナス付与',
};

const getOperationLabel = (operationType: string): string => {
  return operationTypeLabels[operationType] || operationType;
};

const KujiPointPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [points, setPoints] = useState<number>(0);
  const [transactions, setTransactions] = useState<AccountTransaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState<boolean>(false);
  const [expiringSoon, setExpiringSoon] = useState<number>(0);
  const [nextExpiration, setNextExpiration] = useState<{ expires_at: string; amount: number } | null>(null);

  // Load transactions
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) return;

      setLoadingTransactions(true);
      try {
        const response = await fetchAccountTransactions();
        setTransactions(response.transactions);
        setExpiringSoon(response.expiring_soon);
        setNextExpiration(response.next_expiration);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoadingTransactions(false);
      }
    };

    loadData();
  }, [currentUser]);

  // Update points display when user data changes
  useEffect(() => {
    if (currentUser && currentUser.balance !== undefined) {
      setPoints(currentUser.balance);
    }
  }, [currentUser]);

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  // 入金（獲得）トランザクション
  const earnedTransactions = transactions.filter(t => t.amount > 0);
  // 出金（利用）トランザクション
  const usageTransactions = transactions.filter(t => t.amount < 0);

  return (
    <>
      <Helmet>
        <title>ポイント残高</title>
        <meta property="og:title" content="ポイント残高" />
      </Helmet>

      <div className="min-h-screen bg-theme-bg">
        <div>
          <div className="max-w-screen-lg mx-auto px-4 pb-4 sm:pb-8 pt-20">
            <div className="flex gap-8">
              {/* サイドバー - デスクトップのみ */}
              <div className="hidden lg:block">
                <UserSidebar />
              </div>

              {/* メインコンテンツ */}
              <section className="flex-1 min-w-0">
                {/* タイトルセクション */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-theme-primary border-theme rounded-theme flex items-center justify-center shadow-theme-sm">
                    <Coins className="h-5 w-5 text-theme-button" />
                  </div>
                  <div>
                    <h1 className="text-xl [font-weight:var(--theme-font-weight)] text-theme-bg-text">ポイント残高</h1>
                    <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-theme-bg-text opacity-70">ポイント残高と取引履歴を確認できます</p>
                  </div>
                </div>

                {/* コンテンツカード */}
                <div className="p-4 sm:p-6 bg-white border-theme rounded-theme-lg shadow-theme">
                  {/* 所有ポイント */}
                  <div className="p-4 border-theme rounded-theme bg-gray-100 mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-theme-primary border-theme rounded-theme flex items-center justify-center">
                        <Wallet className="h-4 w-4 text-theme-button" />
                      </div>
                      <h3 className="text-sm [font-weight:var(--theme-font-weight-button)] text-black">現在のポイント</h3>
                    </div>
                    <div className="text-3xl [font-weight:var(--theme-font-weight)] text-black">
                      {formatNumber(points)}
                    </div>
                    <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600 mt-1">ポイント</p>
                  </div>

                  {/* 期限切れ警告バナー */}
                  {expiringSoon > 0 && nextExpiration && (
                    <div className="p-4 border-theme rounded-theme bg-yellow-50 border-yellow-200 mb-6">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-yellow-400 border-theme rounded-theme flex items-center justify-center flex-shrink-0">
                          <AlertTriangle className="h-4 w-4 text-black" />
                        </div>
                        <div>
                          <h3 className="text-sm [font-weight:var(--theme-font-weight)] text-black">ポイントの有効期限が近づいています</h3>
                          <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-700 mt-1">
                            {formatNumber(expiringSoon)}ポイントが30日以内に期限切れになります。
                            次の期限: {new Date(nextExpiration.expires_at).toLocaleDateString('ja-JP')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 履歴タブ */}
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 bg-theme-primary border-theme rounded-theme flex items-center justify-center">
                        <History className="h-4 w-4 text-theme-button" />
                      </div>
                      <h2 className="text-lg [font-weight:var(--theme-font-weight)] text-black">履歴</h2>
                    </div>

                    <Tabs defaultValue="usage" className="w-full">
                      <TabsList className="grid w-full grid-cols-2 mb-4 bg-gray-100 border-theme rounded-theme p-1">
                        <TabsTrigger value="usage" className="[font-weight:var(--theme-font-weight-button)] data-[state=active]:bg-theme-primary data-[state=active]:text-theme-button rounded-md">利用履歴</TabsTrigger>
                        <TabsTrigger value="earned" className="[font-weight:var(--theme-font-weight-button)] data-[state=active]:bg-theme-primary data-[state=active]:text-theme-button rounded-md">獲得履歴</TabsTrigger>
                      </TabsList>

                      {/* 利用履歴タブ */}
                      <TabsContent value="usage">
                        <div className="border-theme rounded-theme overflow-hidden">
                          {loadingTransactions ? (
                            <div className="flex items-center justify-center py-8">
                              <div className="w-6 h-6 border-theme border-t-transparent rounded-full animate-spin" />
                            </div>
                          ) : usageTransactions.length === 0 ? (
                            <div className="text-center py-8">
                              <p className="[font-weight:var(--theme-font-weight-button)] text-gray-600">利用履歴がありません</p>
                            </div>
                          ) : (
                            <div className="divide-y divide-[var(--theme-border-color)]">
                              {usageTransactions.map((transaction) => (
                                <div
                                  key={transaction.id}
                                  className="flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-theme border-theme flex items-center justify-center bg-red-400">
                                      <TrendingDown className="h-5 w-5 text-black" />
                                    </div>
                                    <div>
                                      <div className="[font-weight:var(--theme-font-weight-button)] text-black">
                                        {getOperationLabel(transaction.operation?.description || transaction.operation_type)}
                                      </div>
                                      {transaction.operation?.event_name && (
                                        <div className="text-sm font-medium text-gray-600">
                                          {transaction.operation.event_name}
                                        </div>
                                      )}
                                      <div className="text-xs font-medium text-gray-500">
                                        {new Date(transaction.created_at).toLocaleString('ja-JP')}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-lg [font-weight:var(--theme-font-weight)] text-red-600">
                                    {formatNumber(transaction.amount)} {transaction.currency}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </TabsContent>

                      {/* 獲得履歴タブ */}
                      <TabsContent value="earned">
                        <div className="border-theme rounded-theme overflow-hidden">
                          {loadingTransactions ? (
                            <div className="flex items-center justify-center py-8">
                              <div className="w-6 h-6 border-theme border-t-transparent rounded-full animate-spin" />
                            </div>
                          ) : earnedTransactions.length === 0 ? (
                            <div className="text-center py-8">
                              <p className="[font-weight:var(--theme-font-weight-button)] text-gray-600">獲得履歴がありません</p>
                            </div>
                          ) : (
                            <div className="divide-y divide-[var(--theme-border-color)]">
                              {earnedTransactions.map((transaction) => {
                                const isExpired = transaction.expired;
                                return (
                                  <div
                                    key={transaction.id}
                                    className={`flex items-center justify-between p-4 hover:bg-gray-50 transition-colors ${isExpired ? 'bg-gray-50' : 'bg-white'}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className={`w-10 h-10 rounded-theme border-theme flex items-center justify-center ${isExpired ? 'bg-gray-300' : 'bg-green-400'}`}>
                                        <TrendingUp className="h-5 w-5 text-black" />
                                      </div>
                                      <div>
                                        <div className={`[font-weight:var(--theme-font-weight-button)] ${isExpired ? 'text-gray-400' : 'text-black'}`}>
                                          {getOperationLabel(transaction.operation?.description || transaction.operation_type)}
                                          {isExpired && <span className="ml-2 text-red-500 text-xs">(期限切れ)</span>}
                                        </div>
                                        {transaction.operation?.event_name && (
                                          <div className={`text-sm font-medium ${isExpired ? 'text-gray-400' : 'text-gray-600'}`}>
                                            {transaction.operation.event_name}
                                          </div>
                                        )}
                                        <div className="text-xs font-medium text-gray-500">
                                          {new Date(transaction.created_at).toLocaleString('ja-JP')}
                                        </div>
                                        {/* 有効期限表示 */}
                                        {transaction.expires_at && (
                                          <div className={`flex items-center gap-1 text-xs font-medium mt-1 ${
                                            isExpired ? 'text-red-400' : 'text-gray-500'
                                          }`}>
                                            <Clock className="h-3 w-3" />
                                            有効期限: {new Date(transaction.expires_at).toLocaleDateString('ja-JP')}
                                            {!isExpired && transaction.remaining_amount !== null && (
                                              <span className="ml-2">
                                                (残: {formatNumber(transaction.remaining_amount)}{transaction.currency})
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className={`text-lg [font-weight:var(--theme-font-weight)] ${
                                      isExpired ? 'text-gray-400 line-through' : 'text-green-600'
                                    }`}>
                                      +{formatNumber(transaction.amount)} {transaction.currency}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </TabsContent>
                    </Tabs>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default KujiPointPage;
